import { DownOutlined } from "@ant-design/icons"
import { Dropdown, Flex, Tag, Tooltip } from "antd"
import React from "react"

import { empty } from "../../../constants/placeholders"
import {
  OPERATIONAL_STATES,
  OPERATIONAL_STATE_LABELS,
  type OperationalState,
  type StoredOperationalState,
  isLiveOverlay,
  operationalStateColorMap
} from "../../Lead/constants/operational-states.ts"
import { LeadPill } from "./LeadPill.tsx"

const VISIBLE_TAGS = 2

const sameStates = (a: StoredOperationalState[], b: StoredOperationalState[]) =>
  a.length === b.length && a.every((state) => b.includes(state))

interface LeadOperationalStatesCellProps {
  states?: StoredOperationalState[] | null
  editable?: boolean
  onChange?: (next: StoredOperationalState[]) => void
}

export function LeadOperationalStatesCell({
  states,
  editable,
  onChange
}: LeadOperationalStatesCellProps) {
  const [draft, setDraft] = React.useState<StoredOperationalState[] | null>(
    null
  )

  const saved = states ?? []
  const current = draft ?? saved
  const known = current.filter(isLiveOverlay)

  const shown = known.slice(0, VISIBLE_TAGS)
  const overflow = known.slice(VISIBLE_TAGS)

  const tags = (
    <>
      {shown.map((state) => (
        <LeadPill key={state} style={operationalStateColorMap[state]}>
          {OPERATIONAL_STATE_LABELS[state]}
        </LeadPill>
      ))}
      {!!overflow.length && (
        <Tooltip
          title={overflow
            .map((state) => OPERATIONAL_STATE_LABELS[state])
            .join(", ")}
        >
          <Tag className={"m-0"}>{`+${overflow.length}`}</Tag>
        </Tooltip>
      )}
    </>
  )

  if (!editable || !onChange) {
    if (!known.length) return <>{empty}</>

    return (
      <Flex wrap gap={4}>
        {tags}
      </Flex>
    )
  }

  // The menu stays open across picks, so each one is staged locally and the
  // whole selection is sent once it closes. Sending per pick would rebase the
  // next one on a list the refetch has not caught up to yet, dropping it.
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraft(saved)
      return
    }

    setDraft(null)
    if (draft && !sameStates(draft, saved)) onChange(draft)
  }

  return (
    <Dropdown
      trigger={["click"]}
      onOpenChange={handleOpenChange}
      menu={{
        multiple: true,
        selectable: true,
        selectedKeys: current,
        items: Object.values(OPERATIONAL_STATES).map((state) => ({
          key: state,
          label: OPERATIONAL_STATE_LABELS[state]
        })),
        onClick: ({ domEvent }) => domEvent.stopPropagation(),
        onSelect: ({ key }) => setDraft([...current, key as OperationalState]),
        onDeselect: ({ key }) =>
          setDraft(current.filter((state) => state !== key))
      }}
    >
      {/* The caret is pinned beside the pills rather than joining the wrap.
          Two pills fill the column, which pushed it onto a line of its own and
          left the row taller and visibly ragged. */}
      <Flex gap={4} align={"center"} className="cursor-pointer flex-nowrap">
        <Flex wrap gap={4} className={"min-w-0"}>
          {known.length ? tags : <Tag className="m-0">{empty}</Tag>}
        </Flex>
        <DownOutlined className="shrink-0 text-xs" />
      </Flex>
    </Dropdown>
  )
}
